import type { SupportContent } from "./types";

export const supportZhHant: SupportContent = {
  locale: "zh-Hant",
  seo: {
    title: "支援 - Prismark",
    description: "Prismark 支援頁面，包含使用說明、常見問題、聯絡電子郵件及隱私權政策連結。"
  },
  nav: {
    home: "Prismark",
    support: "支援",
    privacy: "隱私權政策"
  },
  hero: {
    eyebrow: "Prismark 支援",
    title: "有什麼可以幫你？",
    description: "在這裡可以了解 Prismark 的常見問題，包括相機權限、相簿取用、雙鏡頭錄影支援以及聯絡方式。"
  },
  guide: {
    title: "支援說明",
    body: [
      "Prismark 是一款適用於 iOS 的輕量而專業的相機 App，提供手動參數控制與多種拍攝輔助工具。",
      "如果你在使用過程中遇到問題，可以先查看下方常見問題。需要進一步協助時，請透過電子郵件聯絡我們，並盡量附上裝置型號、iOS 版本、App 版本以及問題描述。"
    ]
  },
  faq: {
    title: "常見問題",
    items: [
      {
        question: "為什麼 Prismark 需要相機權限？",
        answer: "Prismark 需要相機權限來拍照、錄製影片，以及在支援的裝置上使用雙鏡頭錄影等相機相關功能。"
      },
      {
        question: "為什麼 Prismark 需要麥克風權限？",
        answer: "當你錄製有聲音的影片時，需要使用麥克風權限。Prismark 不會在非錄製影片的情況下使用麥克風。"
      },
      {
        question: "為什麼 Prismark 需要相簿權限？",
        answer: "相簿權限用於儲存你拍攝的照片和影片，也用於在需要時讓你在 App 內查看已拍攝的媒體內容。"
      },
      {
        question: "Prismark 會上傳我的照片或影片嗎？",
        answer:
          "Prismark 的核心拍攝流程設計為在裝置本機處理照片和影片。目前版本不會將你的照片或影片上傳到我們的伺服器。"
      },
      {
        question: "Prismark 會收集個人資料嗎？",
        answer: "Prismark 的核心相機功能不需要註冊帳號。關於資料處理的最新說明，請查看隱私權政策。"
      },
      {
        question: "為什麼我的裝置無法使用雙鏡頭錄影？",
        answer: "有兩種可能。雙鏡頭錄影屬於 Prismark Pro，是一次性 App 內購買，未解鎖時會先提示你購買。另外它也仰賴裝置硬體和 iOS 系統支援，部分裝置型號或鏡頭組合無法同時用兩顆鏡頭錄影。"
      },
      {
        question: "Prismark Pro 包含哪些功能？是訂閱嗎？",
        answer: "Prismark Pro 解鎖三項功能：匯入你自己的 LUT、ProRAW 與 RAW 輸出、雙鏡頭子母畫面錄影。Prismark 的其他功能都不額外收費。這是一次性買斷而非訂閱，沒有週期扣款，也不需要取消。"
      },
      {
        question: "怎麼匯入 LUT？支援哪些檔案？",
        answer: "LUT 匯入屬於 Prismark Pro。解鎖後可以在「設定 > LUTs > 匯入 LUT」匯入，也可以點取景框濾鏡列末尾的「+」。Prismark 支援 .cube 檔案和 Hald PNG 圖片，也可以在「檔案」App 裡用「開啟方式」把 .cube 傳給 Prismark。匯入後的 LUT 會排在內建濾鏡旁邊，強度調整方式完全一樣；在「設定 > LUTs」裡可以重新命名、拖移排序或刪除。Prismark 本身不附帶任何 LUT，所以在你匯入之前列表是空的。"
      },
      {
        question: "我買了 Prismark Pro，卻找不到 ProRAW，為什麼？",
        answer: "ProRAW 除了需要購買，還取決於你的裝置。Apple 只在部分 iPhone 機型上支援 ProRAW；在不支援的裝置上，Prismark 會直接隱藏這一組，而不是放一個點了也用不了的選項，所以「設定 > 照片規格」裡不會出現它。LUT 匯入和雙鏡頭錄影是獨立的，不受影響。如果你的裝置確實支援 ProRAW 卻仍然找不到，請寄電子郵件聯絡我們並附上裝置型號。"
      },
      {
        question: "我買了 Prismark Pro，換了新裝置卻顯示未解鎖，怎麼辦？",
        answer: "開啟 Prismark 的設定頁，點頂部的 Prismark Pro 入口，選擇「回復購買」。請確認這台裝置登入的是當初購買時用的那個 Apple 帳號。如果仍然沒有解鎖，請寄電子郵件聯絡我們，附上 Apple 帳號所在地區和大致的購買日期。"
      },
      {
        question: "為什麼錄影品質或效能會有差異？",
        answer: "相機效能可能受到裝置型號、iOS 版本、剩餘儲存空間、電量、機身溫度以及所選拍攝模式影響。"
      },
      {
        question: "如何回報問題或提出功能建議？",
        answer: "請透過電子郵件聯絡我們，並盡量附上裝置型號、iOS 版本、App 版本、重現步驟，以及截圖或螢幕錄影等輔助資訊。"
      }
    ]
  },
  contact: {
    title: "聯絡我們",
    description: "如果你需要支援、回報問題、提出建議，或有隱私相關問題，可以透過電子郵件聯絡我們。",
    emailLabel: "電子郵件",
    email: "axqora@gmail.com",
    emailSubject: "Prismark 支援請求"
  },
  relatedLinks: {
    title: "相關連結",
    links: [
      { label: "隱私權政策", href: "/zh-Hant/privacy" },
      { label: "使用條款", href: "/zh-Hant/terms" }
    ]
  }
};
